import './App.css';
import { GoogleOAuthProvider } from "@react-oauth/google";
import GoogleAutoRefresh from "./components/GoogleAutoRefresh";
import SessionExpiredBanner from "./components/SessionExpiredBanner";
import SessionKeeper from "./components/SessionKeeper";
import GettingStartedLandingView from './views/getting-started/getting-started-landing-view';
import { Route, Routes } from "react-router-dom";
import NewLeagueView from './views/leagues/new-league-view/new-league-view';
import ManageLeagueView from './views/leagues/manage-league-view/manage-league-view';
import JoinLeagueView from './views/leagues/join-league-view/join-league-view';
import SearchView from './views/search/search-view';
import CreateGameView from './views/create-game/create-game-view';
import MyLeaguesView from './views/my-leagues/my-leagues-view';
import MyActivityView from './views/my-activity/my-activity-view';
import PricingView from './views/pricing/pricing-view';
import VenmoManualCheckoffView from './views/payments/venmo-manual-checkoff-view';
import ProfileView from './views/profile/profile-view';
import UnsubscribeView from './views/notifications/unsubscribe-view';
import RsvpView from './views/rsvp/rsvp-view';
import GameRosterView from './views/games/game-roster-view';
import GameSignupView from './views/games/game-signup-view';
import LeagueSignupView from './views/leagues/league-signup-view/league-signup-view';
import WaiverSignView from './views/leagues/waiver-sign-view/waiver-sign-view';
import MySubscriptionView from './views/subscription/my-subscription-view';
import { Box, CssBaseline, ThemeProvider, Typography, createTheme } from "@mui/material";
import { useMemo, useState } from "react";
import TopNavBar from "./components/top-nav-bar";

const THEME_COOKIE_KEY = "puck-pickup-theme";
const THEME_STORAGE_KEY = "puck-pickup-theme-mode";

function getThemeCookie() {
  const match = document.cookie.split("; ").find((c) => c.startsWith(THEME_COOKIE_KEY + "="));
  return match ? match.split("=")[1] : null;
}

function setThemeCookie(value) {
  document.cookie = `${THEME_COOKIE_KEY}=${value}; max-age=31536000; path=/; SameSite=Lax`;
}

function App() {
  const [mode, setMode] = useState(
    () => getThemeCookie() || localStorage.getItem(THEME_STORAGE_KEY) || "light"
  );

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: {
            main: mode === "dark" ? "#5B8DEF" : "#0033A0",
          },
        },
        shape: {
          borderRadius: 8,
        },
        typography: {
          fontSize: 16,
          caption: {
            fontSize: "0.875rem",
          },
        },
        components: {
          MuiAppBar: {
            styleOverrides: {
              root: {
                backgroundColor: "#C8102E",
                color: "#ffffff",
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: ({ theme }) => ({
                "&:not(.MuiAppBar-root)": {
                  ...(theme.palette.mode === "dark" && {
                    backgroundColor: "rgba(10,10,10,1.0)",
                  }),
                  ...(theme.palette.mode === "light" && {
                    backgroundColor: "rgba(255,255,255,1.0)",
                  }),
                },
              }),
            },
          },
          MuiPickersToolbarText: {
            styleOverrides: {
              root: ({ theme }) => ({
                [theme.breakpoints.down("sm")]: {
                  fontSize: "1.9rem",
                },
              }),
            },
          },
          MuiClockNumber: {
            styleOverrides: {
              root: ({ theme }) => ({
                "&.Mui-selected": {
                  backgroundColor: "transparent",
                  color: theme.palette.primary.main,
                  fontWeight: 700,
                },
              }),
            },
          },
          MuiMultiSectionDigitalClockSection: {
            styleOverrides: {
              item: ({ theme }) => ({
                "&.Mui-selected": {
                  backgroundColor: "transparent",
                  color: theme.palette.primary.main,
                  fontWeight: 700,
                  "&:hover": {
                    backgroundColor: "rgba(25, 118, 210, 0.08)",
                  },
                },
              }),
            },
          },
        },
      }),
    [mode]
  );

  const handleThemeChange = (event) => {
    const nextMode = event.target.checked ? "dark" : "light";
    setMode(nextMode);
    setThemeCookie(nextMode);
    localStorage.setItem(THEME_STORAGE_KEY, nextMode);
  };

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ""}>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GoogleAutoRefresh />
      <SessionKeeper />
      <div className="App">
        {/* The router itself is created in index.js. It has to be a data router so pages can hold a navigation
            and ask "leave without saving?" (see components/unsaved-changes.jsx). */}
        <>
          <TopNavBar mode={mode} onToggleTheme={handleThemeChange} />
          <SessionExpiredBanner />
          <Routes>
            <Route path="/*" element={<GettingStartedLandingView />} />
            <Route path="/new-league" element={<NewLeagueView />} />
            <Route path="/search" element={<SearchView />} />
            <Route path="/create-game" element={<CreateGameView />} />
            <Route path="/my-leagues" element={<MyLeaguesView />} />
            <Route path="/my-schedule" element={<MyActivityView />} />
            <Route path="/manage-league/:leagueId" element={<ManageLeagueView />} />
            <Route path="/manage-league/:leagueId/:tab" element={<ManageLeagueView />} />
            <Route path="/join-league" element={<JoinLeagueView />} />
            <Route path="/pricing" element={<PricingView />} />
            <Route path="/payments/venmo-manual" element={<VenmoManualCheckoffView />} />
            <Route path="/profile" element={<ProfileView />} />
            <Route path="/unsubscribe" element={<UnsubscribeView />} />
            <Route path="/rsvp/:token" element={<RsvpView />} />
            <Route path="/games/:gameId/roster" element={<GameRosterView />} />
            <Route path="/games/:gameId/signup" element={<GameSignupView />} />
            <Route path="/leagues/:leagueId/signup" element={<LeagueSignupView />} />
            <Route path="/leagues/:leagueId/waivers/:waiverId/sign" element={<WaiverSignView />} />
            <Route path="/my-subscription" element={<MySubscriptionView />} />
          </Routes>
          <Box
            component="footer"
            sx={{
              py: 2,
              textAlign: "center",
              borderTop: 1,
              borderColor: "divider",
              mt: "auto",
            }}
          >
            <Typography variant="caption" color="text.secondary">
              &copy; {new Date().getFullYear()} Puck Pickup LLC
            </Typography>
          </Box>
        </>
      </div>
    </ThemeProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
